import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';

import axiosInstance from '../../../config/axio.config';
import { useShowMessages } from '../../../hooks/useMessages/useMessages';

// TB_CPAGAR.DESCRICAO no Gerente e VARCHAR(40) — bem menos que o varchar(100) do
// banco do Atendimento. Quem manda e o menor dos dois: passar disso nao trunca la,
// derruba o UPDATE inteiro e o lancamento nunca sincroniza. O backend recusa igual,
// isto so evita a viagem e deixa o contador visivel enquanto a pessoa escreve.
const DESCRIPTION_MAX = 40;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
};

// O centro de custos lido como ele aparece no relatorio: grupo + nome. Um centro
// marcado para nao entrar em relatorio continua na lista — mandar uma despesa para
// la e uma decisao legitima —, mas dito com todas as letras, porque o lancamento
// some do grafico depois de salvo.
const costCenterLabel = (option) =>
  `${option.name}${option.hide_reports === 'Y' ? ' (oculto nos relatórios)' : ''}`;

export default function ExpenseClassificationDialog({ open, expense, costCenters, onClose, onSaved }) {
  const { ShowMessage } = useShowMessages();
  const [description, setDescription] = useState('');
  const [costCenter, setCostCenter] = useState(null);
  const [saving, setSaving] = useState(false);

  const options = useMemo(() => costCenters || [], [costCenters]);

  // Reabrir com outro lancamento tem de recomecar do que esta gravado nele,
  // nunca do que ficou digitado da vez anterior
  useEffect(() => {
    if (!open || !expense) return;
    setDescription(expense.description || '');
  }, [open, expense]);

  // O centro atual so da para marcar depois que a lista chega. Fica em um efeito a
  // parte para uma lista que chegue tarde nao apagar o que ja foi digitado ao lado.
  useEffect(() => {
    if (!open || !expense) return;
    setCostCenter(options.find((c) => c.id === expense.costCenterId) || null);
  }, [open, expense, options]);

  const handleSave = async () => {
    const trimmed = description.trim();
    if (!trimmed) return ShowMessage('error', 'Informe a descrição');
    if (!costCenter) return ShowMessage('error', 'Informe o centro de custos');

    setSaving(true);
    try {
      const { data } = await axiosInstance.patch(`/financeiro/expenses/${expense.id}/classification`, {
        description: trimmed,
        costCenterId: costCenter.id,
      });
      // O backend so responde 'synchronized' depois de o Gerente devolver o que
      // ficou gravado la. Sem essa confirmacao a alteracao esta salva aqui e na
      // fila de envio, entao o aviso tem de ser diferente de um sucesso limpo.
      if (data?.synchronized === false) {
        // 'warning' no hook abre modal com botao de confirmar, nao toast: este aviso
        // nao pode passar despercebido enquanto a pessoa ja olha o proximo lancamento.
        ShowMessage('warning', 'Aguardando o Gerente',
          data.message || 'Lançamento salvo aqui. O envio ao Gerente será repetido automaticamente.');
      } else {
        ShowMessage('success', 'Lançamento atualizado com sucesso!');
      }
      onSaved?.();
      onClose();
    } catch (error) {
      ShowMessage('error', error.response?.data?.message || 'Erro ao salvar o lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Lançamento</DialogTitle>
      <DialogContent>
        {/* O lancamento em si e do Gerente: fornecedor, documento, data e valor ficam
            aqui so para identificar o que esta sendo reclassificado */}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {expense?.supplier || 'Sem fornecedor'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            expense?.documentNumber ? `Documento ${expense.documentNumber}` : null,
            expense?.paymentDate ? `Pago em ${formatDate(expense.paymentDate)}` : null,
            formatCurrency(expense?.amountPaid),
          ].filter(Boolean).join(' · ')}
        </Typography>

        <Box sx={{ mt: 2.5 }}>
          <TextField
            autoFocus
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            helperText={`${description.length}/${DESCRIPTION_MAX}`}
            fullWidth
            variant="filled"
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={options}
            value={costCenter}
            onChange={(event, value) => setCostCenter(value)}
            groupBy={(option) => option.cost_group_name || 'Sem Grupo'}
            getOptionLabel={costCenterLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Centro de Custos" variant="filled" />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingRight: 3 }}>
        <Button onClick={onClose} variant="outlined" color="error" disabled={saving}>Cancelar</Button>
        <Button onClick={handleSave} variant="outlined" color="success" disabled={saving}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
}
